import { Getter, Setter } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { cloneDeep } from 'lodash-es';
import { useCallback } from 'react';
import { IKeyResultDataModel } from '../../../data-models/key-result.data-model';
import {
  createObjective,
  createKeyResult,
  KeyResultPayload,
  EditObjectivePayload,
  ObjectivePayload,
  editObjective,
  deleteObjective,
  EditKeyResultPayload,
  editKeyResult,
  deleteKeyResult,
} from '../../../services/queries/MaggieObjectivesQueries';
import { objectivesByCompanyStateJ } from '../../../services/state/ObjectivesStateJ';

export type KeyResultValues = Omit<KeyResultPayload, 'objectiveId' | 'companyId'>;
export interface CreateObjectiveData extends ObjectivePayload {
  ownerId: number;
  keyResults: KeyResultValues[];
}

// errors should be handled in the caller

/**
 * Creates an objective and then its key results, optimistically updating Jotai state.
 * If the objective creation fails, its key results are not created and an error is thrown.
 * If any of the key results fail to create, returns an array of errors, but the state will still be updated with the successfully created objective and key results.
 * @returns array of errors, if any
 */
export const useObjectiveActions = () => {
  const handleCreateObjective = useAtomCallback(
    useCallback(async (get: Getter, set: Setter, data: CreateObjectiveData) => {
      const { name, ownerId, companyId } = data;

      const objRes = await createObjective({ name, ownerId, companyId, categoryId: -1 });

      const krPayloads = data.keyResults.map((kr) => {
        return { ...kr, objectiveId: objRes.id };
      });

      const krs: IKeyResultDataModel[] = [];
      const errors = [];

      for (const payload of krPayloads) {
        try {
          const kr = await createKeyResult(payload);
          krs.push(kr);
        } catch (err) {
          console.error(err);
          errors.push(err);
        }
      }

      const newObjective = { ...objRes, keyResults: krs };
      const prev = await get(objectivesByCompanyStateJ(data.companyId));
      const next = prev ? [...cloneDeep(prev), newObjective] : [newObjective];
      set(objectivesByCompanyStateJ(data.companyId), next);

      // will be handled in the caller - lets the user know if there were some errors
      return errors;
    }, [])
  );

  const handleEditObjective = useAtomCallback(
    useCallback(async (get: Getter, set: Setter, payload: EditObjectivePayload) => {
      const res = await editObjective(payload);
      const prev = await get(objectivesByCompanyStateJ(payload.companyId));
      const next = prev ? prev.map((obj) => (obj.id === payload.id ? res : obj)) : [res];
      set(objectivesByCompanyStateJ(payload.companyId), next);
      return res;
    }, [])
  );

  const handleCreateKeyResult = useAtomCallback(
    useCallback(async (get: Getter, set: Setter, companyId: number, payload: KeyResultPayload) => {
      const res = await createKeyResult(payload);
      const prev = await get(objectivesByCompanyStateJ(companyId));
      if (!prev) {
        set(objectivesByCompanyStateJ(companyId), null);
        return res;
      }
      const next = prev.map((obj) => {
        if (obj.id !== payload.objectiveId) {
          return obj;
        }
        return {
          ...cloneDeep(obj),
          keyResults: [...cloneDeep(obj.keyResults), res],
        };
      });
      set(objectivesByCompanyStateJ(companyId), next);
      return res;
    }, [])
  );

  const handleEditKeyResult = useAtomCallback(
    useCallback(async (get: Getter, set: Setter, companyId: number, payload: EditKeyResultPayload) => {
      const res = await editKeyResult(payload);
      const prev = await get(objectivesByCompanyStateJ(companyId));
      if (!prev) {
        set(objectivesByCompanyStateJ(companyId), null);
        return res;
      }
      const next = prev.map((obj) => {
        if (obj.id !== payload.objectiveId) {
          return obj;
        }
        return {
          ...cloneDeep(obj),
          keyResults: obj.keyResults.map((kr) => (kr.id === payload.id ? res : kr)),
        };
      });
      set(objectivesByCompanyStateJ(companyId), next);
      return res;
    }, [])
  );

  const handleDeleteKeyResult = useAtomCallback(
    useCallback(async (get: Getter, set: Setter, companyId: number, keyResult: IKeyResultDataModel) => {
      await deleteKeyResult(keyResult.id);
      const prev = await get(objectivesByCompanyStateJ(companyId));
      if (!prev) {
        set(objectivesByCompanyStateJ(companyId), null);
        return;
      }
      const next = prev.map((obj) => {
        if (obj.id !== keyResult.objectiveId) {
          return obj;
        }
        return {
          ...cloneDeep(obj),
          keyResults: obj.keyResults.filter((kr) => kr.id !== keyResult.id),
        };
      });
      set(objectivesByCompanyStateJ(companyId), next);
    }, [])
  );

  const handleDeleteObjective = useAtomCallback(
    useCallback(async (get: Getter, set: Setter, companyId: number, objectiveId: number) => {
      await deleteObjective(objectiveId);
      const prev = await get(objectivesByCompanyStateJ(companyId));
      if (!prev) {
        set(objectivesByCompanyStateJ(companyId), null);
        return;
      }
      set(
        objectivesByCompanyStateJ(companyId),
        prev.filter((obj) => obj.id !== objectiveId)
      );
    }, [])
  );

  return {
    handleCreateObjective,
    handleEditObjective,
    handleDeleteObjective,
    handleCreateKeyResult,
    handleEditKeyResult,
    handleDeleteKeyResult,
  };
};
